import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { cn } from "../lib/utils";
import type { LivreJeuResume } from "./contract";
import { modifieEnClair } from "./dates-en-clair";
import { formatDate } from "./format";
import { ALERTE_GRISE, BOUTON_CONTOUR, CHAMP, LIEN_BOUTON_PLEIN } from "./livres-jeux-commun";
import { messageDEchec, useCreerLivreJeu, useLivresJeu } from "./livres-jeux-requetes";
import { cleDepuisTitre, compterJeu, refusDeCle } from "./livres-jeux-textes";
import { ConsoleShell } from "./shell";
import { Chargement, EcranDErreur, panneAffichee } from "./states";

/**
 * L'accueil du module Livres-jeux (`/livres-jeux`, spec du 03/10/2026) : les livres, leurs
 * scenes et celles qui ont un texte, ce qui est publie dans l'appli ; « Nouveau livre » en cree
 * un, sans scene, et l'ouvre.
 */

export function LivresJeuxAccueilPage() {
  const lecture = useLivresJeu();
  const panne = panneAffichee(lecture);
  const [formulaire, setFormulaire] = useState<"ferme" | "ouvert" | "annule">("ferme");
  const bouton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (formulaire === "annule") bouton.current?.focus();
  }, [formulaire]);

  return (
    <ConsoleShell connecte={lecture.isSuccess}>
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <h1 className="text-2xl font-semibold">Livres-jeux</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Les livres dont vous êtes le héros : la trame, les scènes, le PDF et l'appli compagnon.
          </p>
        </div>
        {lecture.isSuccess ? (
          <button
            aria-expanded={formulaire === "ouvert"}
            className={LIEN_BOUTON_PLEIN}
            onClick={() => setFormulaire("ouvert")}
            ref={bouton}
            type="button"
          >
            Nouveau livre
          </button>
        ) : null}
      </header>
      {lecture.isPending ? <Chargement quoi="des livres" /> : null}
      {panne ? <EcranDErreur error={lecture.error} quoi="les livres" /> : null}
      {formulaire === "ouvert" && lecture.isSuccess ? (
        <NouveauLivre fermer={() => setFormulaire("annule")} />
      ) : null}
      {lecture.data && !panne ? <Livres livres={lecture.data.livres} /> : null}
    </ConsoleShell>
  );
}

function Livres({ livres }: { livres: readonly LivreJeuResume[] }) {
  const maintenant = Date.now();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base" id="livres-jeux-livres">
          Livres
        </CardTitle>
      </CardHeader>
      <CardContent>
        {livres.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucun livre pour l'instant : « Nouveau livre », puis une trame et des scènes, ou un YAML
            entier à importer.
          </p>
        ) : (
          <ul aria-labelledby="livres-jeux-livres" className="flex flex-col">
            {livres.map((livre) => (
              <LigneLivre key={livre.id} livre={livre} maintenant={maintenant} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function LigneLivre({ livre, maintenant }: { livre: LivreJeuResume; maintenant: number }) {
  const part = livre.scenes === 0 ? 0 : Math.min(100, (livre.ecrites / livre.scenes) * 100);
  const modifie = modifieEnClair(livre.maj_le, maintenant);
  const publication = livre.publication;
  return (
    <li className="grid gap-x-6 gap-y-2 border-t border-border py-3 first:border-t-0 first:pt-0 last:pb-0 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto_auto] md:items-center">
      <div className="min-w-0">
        <Link
          className="font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          params={{ id: String(livre.id) }}
          search={{ onglet: "scenes" }}
          to="/livres-jeux/livres/$id"
        >
          {livre.titre}
        </Link>
        <p className="font-mono text-xs text-muted-foreground">{livre.cle}</p>
      </div>
      <div className="min-w-0">
        <p className="text-sm tabular-nums">
          {livre.scenes === 0
            ? "Aucune scène"
            : `${compterJeu(livre.ecrites, "scène écrite", "scènes écrites")} sur ${livre.scenes}`}
        </p>
        <span
          aria-hidden="true"
          className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-muted"
        >
          <span className="block h-full bg-foreground" style={{ width: `${part}%` }} />
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        {modifie === "" ? null : `modifié ${modifie}`}
      </p>
      <p
        className={cn(
          "text-sm",
          publication?.recu ? "text-provenance-ecrit" : "text-muted-foreground",
        )}
      >
        {publication === null
          ? "jamais publié"
          : publication.recu
            ? `publié le ${formatDate(publication.publie_le)}`
            : "dernière publication refusée"}
      </p>
    </li>
  );
}

/**
 * « Nouveau livre » : le titre, et l'identifiant propose depuis lui tant qu'on ne le touche pas.
 * L'identifiant est celui du livre dans le format du moteur ; il ne change plus ensuite.
 */
function NouveauLivre({ fermer }: { fermer: () => void }) {
  const navigate = useNavigate();
  const creer = useCreerLivreJeu();
  const [titre, setTitre] = useState("");
  const [cleSaisie, setCleSaisie] = useState<string | null>(null);
  const [tente, setTente] = useState(false);
  const champTitre = useRef<HTMLInputElement>(null);
  const idTitre = useId();
  const idCle = useId();

  useEffect(() => {
    champTitre.current?.focus();
  }, []);

  const cle = cleSaisie ?? cleDepuisTitre(titre);
  const refusTitre = titre.trim() === "" ? "Le titre est vide." : null;
  const refusCle = refusDeCle(cle);
  const montrerTitre = tente && refusTitre !== null;
  const montrerCle = (tente || cleSaisie !== null) && refusCle !== null;

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setTente(true);
    if (refusTitre !== null || refusCle !== null || creer.isPending) return;
    creer.mutate(
      { cle, titre: titre.trim() },
      {
        onSuccess: (reponse) => {
          void navigate({
            to: "/livres-jeux/livres/$id",
            params: { id: String(reponse.livre.id) },
            search: { onglet: "trame" },
          });
        },
      },
    );
  };

  return (
    <section
      aria-labelledby="livres-jeux-nouveau-livre"
      className="max-w-3xl rounded-lg border border-border bg-card p-5 text-card-foreground"
    >
      <h2 className="mb-4 text-base font-semibold" id="livres-jeux-nouveau-livre">
        Nouveau livre
      </h2>
      <form className="flex flex-col gap-4" noValidate onSubmit={envoyer}>
        <div className="grid items-start gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold" htmlFor={idTitre}>
              Titre
            </label>
            <input
              aria-describedby={montrerTitre ? `${idTitre}-refus` : undefined}
              aria-invalid={montrerTitre || undefined}
              className={CHAMP}
              id={idTitre}
              maxLength={200}
              onChange={(evenement) => setTitre(evenement.target.value)}
              placeholder="Le manoir de l'horloger"
              ref={champTitre}
              value={titre}
            />
            {montrerTitre ? (
              <p className="text-sm text-destructive" id={`${idTitre}-refus`}>
                {refusTitre}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold" htmlFor={idCle}>
              Identifiant
            </label>
            <input
              aria-describedby={montrerCle ? `${idCle}-aide ${idCle}-refus` : `${idCle}-aide`}
              aria-invalid={montrerCle || undefined}
              autoCapitalize="none"
              autoCorrect="off"
              className={cn(CHAMP, "font-mono")}
              id={idCle}
              maxLength={60}
              onChange={(evenement) => setCleSaisie(evenement.target.value)}
              placeholder="se remplit depuis le titre"
              spellCheck={false}
              value={cle}
            />
            <p className="text-xs text-muted-foreground" id={`${idCle}-aide`}>
              Minuscules, chiffres et tirets : l'identifiant du livre dans le format du moteur et
              dans les commandes de Claude. Il ne change plus ensuite.
            </p>
            {montrerCle ? (
              <p className="text-sm text-destructive" id={`${idCle}-refus`}>
                {refusCle}
              </p>
            ) : null}
          </div>
        </div>
        <p className={ALERTE_GRISE}>
          Le livre naît vide : la trame d'abord, puis les scènes une à une, ou un livre entier en
          YAML (onglet Livre, « Importer »).
        </p>
        {creer.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {messageDEchec(creer.error)}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            aria-disabled={creer.isPending || undefined}
            className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            type="submit"
          >
            Créer le livre
          </Button>
          <button className={BOUTON_CONTOUR} onClick={fermer} type="button">
            Annuler
          </button>
        </div>
      </form>
    </section>
  );
}
